/**
 * Functions for default keyboard navigation of Tree.
 */
import _findIndex from 'lodash/findIndex';
import { CONSTANTS } from 'common/ListKeyboardNav';
import KEY_CODES from 'common/KeyCodes';
import TREE_CONSTANTS from './constants';

const {
  ARROW_UP,
  ARROW_DOWN,
  ARROW_LEFT,
  ARROW_RIGHT,
  TAB,
  HOME,
  END,
  PAGE_UP,
  PAGE_DOWN,
} = KEY_CODES;
const keyboardEnterKeys = [ARROW_UP, ARROW_DOWN, ARROW_RIGHT, ARROW_LEFT];

const { LIST_WRAPPER, LIST_ITEM_TABABLE_ITEM, LIST_ITEM_SELECTED } = CONSTANTS;
const { TREE_NODE, TREE_BRANCH, TREE_BRANCH_LIST } = TREE_CONSTANTS;

// when list gets focus, transfer focus to the 1st tree item
const handleListFocus = (props, listRef, wasTabPressed, tabPressed, uniqueTreeId: string) => (event) => {
  const { onFocus, keyboardNavigation, keyboardFocus, preventFocusHandling } = props;
  onFocus && onFocus(event);
  if (preventFocusHandling === true) {
    return;
  }
  if (!keyboardNavigation && !keyboardFocus) {
    return;
  }
  if (wasTabPressed) {
    tabPressed && tabPressed(false);
    return;
  }
  tabPressed && tabPressed(false);
  const wrapperDiv = listRef.current.querySelector(`.${LIST_WRAPPER}`);
  const current = document.activeElement;
  const focusedOnListWrapper = current.classList.contains(LIST_WRAPPER);
  if (focusedOnListWrapper) {
    wrapperDiv && wrapperDiv.blur();
    // get all list items
    const tabableClassName = uniqueTreeId ? `${LIST_ITEM_TABABLE_ITEM}-${uniqueTreeId}` : `${LIST_ITEM_TABABLE_ITEM}`;
    const elements = listRef.current.querySelectorAll(`.${tabableClassName}`);
    // if any list item is selected, focus on the next one (if current one is not last in list)
    const selectedDivs = listRef.current.querySelectorAll(`.${LIST_ITEM_SELECTED}`);
    const selectedDiv = selectedDivs && selectedDivs[0];
    if (selectedDiv) {
      selectedDiv.focus();
    } else {
      // focus on 1st list item
      const first = elements[0];
      first && first.focus();
    }
  }
};


// find focusable parent of element and focus it
const focusElementParent = (event, element) => {
  event.preventDefault();
  const elementParentNode = element.parentNode.parentNode;
  const treeElementIsNode = element.classList.contains(TREE_NODE);
  if (treeElementIsNode) {
    const isListParent = elementParentNode.classList.contains(TREE_BRANCH_LIST);
    if (isListParent) {
      elementParentNode && elementParentNode.parentNode && elementParentNode.parentNode.firstChild && elementParentNode.parentNode.firstChild.focus();
    } else {
      elementParentNode && elementParentNode.firstChild && elementParentNode.firstChild.focus();
    }
    return;
  }
  const treeBranchParent = elementParentNode.querySelector((`.${TREE_BRANCH}`));
  const treeBranchParentIsList = elementParentNode.classList.contains(TREE_BRANCH_LIST);
  if (treeBranchParentIsList) {
    const focusOnElement = treeBranchParent.parentNode && treeBranchParent.parentNode.parentNode && treeBranchParent.parentNode.parentNode.previousElementSibling;
    event.preventDefault();
    event.stopPropagation();
    focusOnElement && focusOnElement.focus();
  }
};

// keyboard navigation
const handleListKeyDown = (props: { itemsPerPage: number, keyboardNavigation: boolean, onKeyDown: any }, listRef, tabPressed, uniqueTreeId: string) => (event) => {
  if (!(listRef && listRef.current) || !props) {
    return;
  }
  const { onKeyDown, keyboardNavigation, itemsPerPage } = props;
  onKeyDown && onKeyDown(event);
  if (!keyboardNavigation) {
    return;
  }
  const { key } = event;
  if (key === TAB) {
    // need to know when tab was pressed to set tabIndex of the wrapper.
    tabPressed && tabPressed(true);
    return;
  }
  tabPressed && tabPressed(false);

  const tabableClassName = uniqueTreeId ? `${LIST_ITEM_TABABLE_ITEM}-${uniqueTreeId}` : `${LIST_ITEM_TABABLE_ITEM}`;
  const elements = listRef.current.querySelectorAll(`.${tabableClassName}`);
  const inCurrentList = event.target && event.target.classList && event.target.classList.contains(tabableClassName);
  if (!inCurrentList) {
    return;
  }
  const first = elements[0];
  const last = elements[elements.length - 1];
  const current = document.activeElement;
  const shouldNotFocus = element => !element.classList.contains(tabableClassName) || element.disabled;
  // focus on 1st item if the current is the list wrapper
  const focusedOnListWrapper = current.classList.contains(LIST_WRAPPER);
  if (keyboardEnterKeys.includes(key) && focusedOnListWrapper) {
    event.preventDefault();
    first && first.focus();
    return;
  }
  // focus on the actual list item component
  const currentIndex = _findIndex(elements, elem => (elem as HTMLElement).contains(current));
  const numberOfElements = elements.length;
  const prevItem: (number) => HTMLElement | number | false = itemIndex => itemIndex > 0 && elements[itemIndex - 1];
  const nextItem: (number) => HTMLElement | number | false = itemIndex => itemIndex < numberOfElements && elements[(itemIndex as number) + 1];
  const pageUpItem = (itemIndex) => {
    const itemInd = Math.max(itemIndex - itemsPerPage, 0);
    return elements[itemInd];
  };
  const pageDownItem = (itemIndex:number) => {
    const itemInd = Math.min(itemIndex + itemsPerPage, numberOfElements - 1);
    return elements[itemInd];
  };
  const shiftFocus = (currentItemIndex: {}, traverse: (number: any) => number | false | HTMLElement) => {
    let loopedOnce = false;
    let nextFocus = traverse(currentItemIndex);
    while (nextFocus) {
      if (nextFocus === first) {
        if (loopedOnce) return false;
        loopedOnce = true;
      }
      if (shouldNotFocus(nextFocus)) {
        nextFocus = traverse(nextFocus);
      } else {
        (nextFocus as unknown as HTMLElement).focus();
        return true;
      }
    }
    return false;
  };
  switch (key) {
    case ARROW_UP:
      event.preventDefault();
      event.stopPropagation();
      shiftFocus(currentIndex, prevItem);
      break;
    case ARROW_DOWN:
      event.preventDefault();
      shiftFocus(currentIndex, nextItem);
      break;
    case ARROW_RIGHT:
      // move focus to next child if this is an opened TreeBranch
      // event only gets this far after the TreeBranch has been opened
      event.preventDefault();
      shiftFocus(currentIndex, nextItem);
      break;
    case ARROW_LEFT:
      // move focus to parent if there is a parent
      event.preventDefault();
      event.stopPropagation();
      focusElementParent(event, elements[currentIndex]);
      break;
    case PAGE_UP:
      event.preventDefault();
      event.stopPropagation();
      shiftFocus(currentIndex, pageUpItem);
      break;
    case PAGE_DOWN:
      event.preventDefault();
      event.stopPropagation();
      shiftFocus(currentIndex, pageDownItem);
      break;
    case HOME:
      event.preventDefault();
      first && first.focus();
      break;
    case END:
      event.preventDefault();
      last && last.focus();
      break;
    default:
      break;
  }
};

export { handleListKeyDown, handleListFocus };